import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { convertTimeValueToTimeString } from "@/modules/duration/timeConverter"
import { cn } from "@/modules/lib/utils"
import { ChronoRecapBloc } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/chronoRecapBloc"
import {
  convertChronoFormikToChronoDraft,
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import type { SimpleFormik } from "@/typings/formik"
import { ChronoIcon } from "@images/svgIcons/chronoIcon"
import { EmomIcon } from "@images/svgIcons/emomIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { displayTotalTime } from "@modules/chrono/chronoUtils"
import { word } from "@modules/core/utils/i18n"
import { useMemo } from "react"

export function ChronoEmomRecap({
  formik,
  className,
  ...props
}: { formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey> } & React.ComponentProps<"div">) {
  const displayChronoTime = useMemo(() => {
    const chronoDraft = convertChronoFormikToChronoDraft(formik.values)

    if (chronoDraft === undefined) return word("chrono.title.no_chrono")

    return displayTotalTime(chronoDraft)
  }, [formik.values])

  const onDeleteChronoClick = () => {
    formik.setFieldValue("chronoEmom", undefined)
    formik.setFieldValue("chrono", undefined)
    formik.setFieldValue("chronoType", undefined)
  }

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      <div className="mb-2 flex flex-row items-start justify-between">
        <EmomIcon color="currentColor" className="text-textOnColor-body" />

        <div className="flex flex-col items-center">
          <Text fontStyle="LS">{word("chrono.title.emom")}</Text>

          <div className="flex items-center">
            <ChronoIcon color="currentColor" className="text-textDefault-body" />
            <Text className="ml-2" fontStyle="TL">
              {displayChronoTime}
            </Text>
          </div>
        </div>

        <Button variant="ghost" size="iconMd" onClick={onDeleteChronoClick}>
          <TrashIcon color="currentColor" className="text-textDefault-body" width={22} height={22} />
        </Button>
      </div>

      {formik.values.chronoEmom?.trainingTime && (
        <>
          <div className="mb-2 flex items-center justify-between gap-2">
            <ChronoRecapBloc
              playIcon
              value={convertTimeValueToTimeString(formik.values.chronoEmom.trainingTime, "00m 00s")}
            />

            <ChronoRecapBloc
              circleTimeIcon
              value={`${formik.values.chronoEmom.numberOfRounds} ${word("global.score.round.unit", { count: formik.values.chronoEmom.numberOfRounds })}`}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <ChronoRecapBloc
              setsIcon
              value={`${formik.values.chronoEmom.numberOfSets} ${word("global.score.set.unit", { count: formik.values.chronoEmom.numberOfSets })}`}
            />
            <ChronoRecapBloc
              circleBreakIcon
              value={`${convertTimeValueToTimeString(formik.values.chronoEmom.restBetweenSetsTime, "00m 00s")} OFF`}
            />
          </div>
        </>
      )}
    </div>
  )
}
